import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { Camera, Layers, X } from 'lucide-react';
import { SectionTitle } from './SectionTitle';
import { WORK_ORDER_LABELS, workOrderSectionTestId } from '../../workOrderSections';
import { groupByYear } from '../../../../Helpers/photoOrdering';
import PhotoYearHeading from '../../../../Components/PhotoYearHeading';
import ConfirmationModal from '../../../../Components/ConfirmationModal';
import './shared.css';

export interface PhotoGroupItem {
    key: string;
    url: string;
    /** False hides the remove button for this photo (e.g. not the viewer's own). Defaults to true. */
    canRemove?: boolean;
    thumbnailUrl?: string;
    /** Stored photo date; drives the year headings. */
    date?: string;
}

export interface PhotoGroupProps {
    title: string;
    /** Newest first (`compareNewestFirst`); shown under year headings when they span more than one year. */
    photos: PhotoGroupItem[];
    emptyText?: string;
    onAdd?: () => void;
    /** Shows a spinner in the add button and disables it while files upload */
    uploading?: boolean;
    /** Called once the user has confirmed the removal in the modal. */
    onRemove?: (key: string) => void;
    onOpen?: (key: string) => void;
    addLabel?: string;
    /** Optional wrapper testid; sections wrapping a single group pass theirs */
    testId?: string;
    /** 'section' on its own (Foton från felanmälan), 'sub' inside Dokumentation */
    level?: 'section' | 'sub';
}

/**
 * Titled responsive grid of square thumbnails. No section testid of its own:
 * it is used inside the issue-photos and documentation sections.
 */
export const PhotoGroup: React.FC<PhotoGroupProps> = ({
    title,
    photos,
    emptyText = 'Inga foton',
    onAdd,
    uploading = false,
    onRemove,
    onOpen,
    addLabel = WORK_ORDER_LABELS.add,
    testId,
    level = 'sub'
}) => {
    const [removeKey, setRemoveKey] = useState<string | null>(null);

    const handleRemoveConfirm = () => {
        if (removeKey && onRemove) onRemove(removeKey);
        setRemoveKey(null);
    };

    return (
        <div className={`wo-photo-group wo-photo-group--${level}${level === 'section' ? ' wo-card' : ''}`} data-testid={testId}>
            <SectionTitle
                icon={Camera}
                title={title}
                count={photos.length}
                level={level}
                onAdd={onAdd}
                addLabel={addLabel}
                adding={uploading}
            />
            {photos.length === 0 ? (
                <p className="text-muted small mb-0">{emptyText}</p>
            ) : (
                groupByYear(photos).map(({ year, items }) => (
                    <div key={year ?? ''} className={year ? 'mb-2' : undefined}>
                        {year && <PhotoYearHeading year={year} />}
                        <div className="wo-photo-grid">
                            {items.map((photo) => (
                                <div key={photo.key} className="wo-photo-grid__item">
                                    <button
                                        type="button"
                                        className="wo-photo-grid__button"
                                        onClick={onOpen ? () => onOpen(photo.key) : undefined}
                                        disabled={!onOpen}
                                        aria-label="Visa foto"
                                    >
                                        <img
                                            src={photo.thumbnailUrl ?? photo.url}
                                            alt=""
                                            loading="lazy"
                                            className="wo-photo-grid__image"
                                        />
                                    </button>
                                    {onRemove && photo.canRemove !== false && (
                                        <Button
                                            variant="light"
                                            size="sm"
                                            className="wo-photo-grid__remove shadow-sm"
                                            aria-label="Ta bort foto"
                                            onClick={() => setRemoveKey(photo.key)}
                                        >
                                            <X size={14} strokeWidth={2.5} aria-hidden="true" />
                                        </Button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                ))
            )}
            {onRemove && (
                <ConfirmationModal
                    show={removeKey !== null}
                    title="Ta bort foto"
                    message="Vill du ta bort fotot?"
                    confirmText="Ta bort foto"
                    onConfirm={handleRemoveConfirm}
                    onCancel={() => setRemoveKey(null)}
                />
            )}
        </div>
    );
};

export interface IssuePhotosSectionProps {
    photos: PhotoGroupItem[];
    onOpen?: (key: string) => void;
    emptyText?: string;
}

/** Section 7: read-only photos from the originating issue report. */
export const IssuePhotosSection: React.FC<IssuePhotosSectionProps> = ({ photos, onOpen, emptyText }) => (
    <PhotoGroup
        testId={workOrderSectionTestId('issuePhotos')}
        title={WORK_ORDER_LABELS.sectionIssuePhotos}
        level="section"
        photos={photos}
        onOpen={onOpen}
        emptyText={emptyText}
    />
);

export interface DocumentationSectionProps {
    title?: string;
    /** Slot next to the heading, e.g. an "Ändra dokumentation" toggle */
    headerExtra?: React.ReactNode;
    children: React.ReactNode;
}

/** Section 9: heading + optional control slot + the documentation groups. */
export const DocumentationSection: React.FC<DocumentationSectionProps> = ({
    title = WORK_ORDER_LABELS.sectionDocumentation,
    headerExtra,
    children
}) => (
    <div className="wo-documentation wo-card" data-testid={workOrderSectionTestId('documentation')}>
        <SectionTitle icon={Layers} title={title} extra={headerExtra} />
        <div className="wo-documentation__groups">{children}</div>
    </div>
);

export default PhotoGroup;
